/**
 * 관계선을 맵 위에 그린다 — 왼쪽 이웃은 파랑 실선, 오른쪽 이웃은 초록 점선.
 *
 * 이 관계는 객체 properties 가 아니라 현장 DB 테이블에 사는 값이라
 * editor/lib/relationshipVisualization(선택 객체의 properties 기반)으로는 못
 * 그린다. 임시 링크를 직접 넣고 표식(data.__cctvRelation)으로 되찾아 지운다 —
 * 그래프는 주기적으로 자동 저장되므로(graphAutoSave) 남아 있으면 맵 데이터에
 * 섞인다. 그래서 기존 관계선과 같은 isRelationshipVisualization 표식도 함께
 * 달아 둔다 — useRelationshipOverlay 의 scrubLegacyRelationshipLinkCells 가
 * 다음 로드에서 잔존물을 걷어낸다.
 */

import { dia, shapes } from '@joint/core'

import { getActiveCanvasRenderer } from '../../viewer/canvas/activeRenderer'
import type { CctvConnection } from '../model/types'
import { cctvElementsByName } from './graphAdapter'

/** 임시 링크 표식. 맵 데이터로 저장되지 않아야 하는 값이다. */
export const RELATION_LINK_FLAG = '__cctvRelation'

const LEFT_COLOR = '#2563eb' // 파랑 = 왼쪽 이웃
const RIGHT_COLOR = '#16a34a' // 초록 = 오른쪽 이웃

/** 이전에 그린 관계선을 모두 지운다. */
export function clearRelationLinks(graph: dia.Graph | null): void {
  getActiveCanvasRenderer()?.setCctvRelationLines([])
  if (!graph) return
  const stale = graph.getLinks().filter((link) => {
    const data = (link.get('data') as Record<string, unknown>) || {}
    return data[RELATION_LINK_FLAG] === true
  })
  if (stale.length > 0) graph.removeCells(stale)
}

/**
 * rows 를 관계선으로 다시 그린다. Canvas 에디터에서는 쌍마다 한 줄이고, 색은 그
 * 쌍에서 먼저 나온 행의 방향이다. 맵에 없는 이름(다른 층 카메라 등)은 건너뛰고,
 * 건너뛴 행 수를 돌려준다 — 화면에 "관계는 있는데 선이 없다" 를 설명해야 한다.
 */
export function drawRelationLinks(
  graph: dia.Graph | null,
  rows: CctvConnection[],
  strokeWidth: number,
): { drawn: number; skipped: number } {
  clearRelationLinks(graph)
  if (!graph || rows.length === 0) return { drawn: 0, skipped: rows.length }

  const byName = cctvElementsByName(graph)
  // Canvas 에디터(기본)에서는 paper 가 얼어 있어 링크 셀이 화면에 그려지지 않는다
  // — 렌더러 오버레이로 그리고 그래프는 건드리지 않는다.
  const renderer = getActiveCanvasRenderer()
  const lines: { x1: number; y1: number; x2: number; y2: number; color: string }[] = []
  const drawnPairs = new Set<string>()
  const links: dia.Link[] = []
  let skipped = 0
  for (const row of rows) {
    const from = byName.get(row.name)
    const to = byName.get(row.to)
    if (!from || !to) {
      skipped++
      continue
    }
    if (renderer) {
      // 한 쌍의 두 행(좌/우)은 같은 선분이라 나중 색이 앞 색을 덮는다 — 쌍마다
      // 처음 만난 행의 방향 색으로 한 번만 그린다(호출부가 기준 CCTV 행을 앞에 둔다).
      const key = row.name < row.to ? `${row.name}|${row.to}` : `${row.to}|${row.name}`
      if (drawnPairs.has(key)) continue
      drawnPairs.add(key)
      const a = from.getBBox().center()
      const b = to.getBBox().center()
      const color = row.direction === 'left' ? LEFT_COLOR : RIGHT_COLOR
      lines.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y, color })
      continue
    }
    const link = new shapes.standard.Link({
      source: { id: from.id },
      target: { id: to.id },
    })
    const head = strokeWidth * 4
    link.attr({
      line: {
        stroke: row.direction === 'left' ? LEFT_COLOR : RIGHT_COLOR,
        strokeWidth,
        strokeDasharray:
          row.direction === 'left' ? 'none' : `${strokeWidth * 3} ${strokeWidth * 2}`,
        targetMarker: {
          type: 'path',
          d: `M ${head} -${strokeWidth * 2} 0 0 ${head} ${strokeWidth * 2} z`,
        },
      },
      // 선을 클릭해 쌍을 빼므로 클릭 표적을 넓힌다(투명 선).
      wrapper: { strokeWidth: strokeWidth * 6, cursor: 'pointer' },
    })
    // name/to — 선을 클릭했을 때 어느 쌍인지 되찾는다.
    link.set('data', { [RELATION_LINK_FLAG]: true, name: row.name, to: row.to })
    // 기존 관계선 스크러버가 잔존물을 지울 수 있게 같은 표식을 단다.
    link.set('isRelationshipVisualization', true)
    links.push(link)
  }
  if (renderer) {
    renderer.setCctvRelationLines(lines)
    return { drawn: lines.length, skipped }
  }
  if (links.length > 0) graph.addCells(links)
  return { drawn: links.length, skipped }
}

/**
 * world 좌표 p 에서 tol 안에 있는 관계선의 쌍(이름 둘). Canvas 에디터는 선이
 * 셀이 아니라 클릭 이벤트가 없으므로, 선을 클릭해 빼려면 여기서 거리로 찾는다.
 */
export function relationPairAt(
  graph: dia.Graph | null,
  rows: CctvConnection[],
  p: { x: number; y: number },
  tol: number,
): [string, string] | null {
  const byName = cctvElementsByName(graph)
  let best: [string, string] | null = null
  let bestDist = tol
  for (const row of rows) {
    const from = byName.get(row.name)
    const to = byName.get(row.to)
    if (!from || !to) continue
    const d = segmentDistance(p, from.getBBox().center(), to.getBBox().center())
    if (d <= bestDist) {
      bestDist = d
      best = [row.name, row.to]
    }
  }
  return best
}

/**
 * world 좌표 p 에서 가장 가까운 CCTV(테두리에서 tol 안). 캔버스 hitTest 는 맨 위
 * 셀(구역·주차면)을 돌려줘 그 아래 카메라를 못 집으므로 CCTV 만 따로 찾는다.
 */
export function cctvAt(
  graph: dia.Graph | null,
  p: { x: number; y: number },
  tol: number,
): dia.Element | null {
  let best: dia.Element | null = null
  let bestDist = Infinity
  for (const element of cctvElementsByName(graph).values()) {
    const box = element.getBBox()
    if (!box.clone().inflate(tol).containsPoint(p)) continue
    const c = box.center()
    const d = Math.hypot(p.x - c.x, p.y - c.y)
    if (d < bestDist) {
      bestDist = d
      best = element
    }
  }
  return best
}

function segmentDistance(
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}
